import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {
    FloorplanListHalfboardDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanListHalfboard.dto";
import {
    ReservationPreviewCardContentProps
} from "../read/dialogs/ReservationPreviewCardContent";
import {DragData, FloorplanTable} from "../types";
import {
    getFloorplanGuestCount,
    getFloorplanGuestCounts,
    getGuestCount,
    getHalfboardRateLabel,
} from "./reservation-utils";

interface TableCapacityCheck {
    capacity: number;
    currentGuestCount: number;
    freeSeats: number;
}

export interface DropConflictResult extends TableCapacityCheck {
    targetTable: FloorplanTable;
    conflicts: FloorplanReservationItemDTO[];
    hasProblems: boolean;
}

type ReservationDragData = Extract<DragData, { type: "unassigned" | "assigned" }>;

export type DropDecision =
    | { action: "noop" }
    | {
    action: "execute_reservation_drop";
    data: ReservationDragData;
    targetTableId: string;
    result: DropConflictResult;
    neededSeats: number;
}
    | {
    action: "open_conflict_dialog";
    data: DragData;
    targetTableId: string;
    result: DropConflictResult;
    neededSeats: number;
}
    | {
    action: "open_shared_table_dialog";
    data: ReservationDragData;
    targetTableId: string;
    result: DropConflictResult;
}
    | {
    action: "open_halfboard_period";
    halfboard: FloorplanListHalfboardDTO;
    targetTable: FloorplanTable;
};

const floorToPrevMinute = (ms: number) => Math.floor(ms / 60000) * 60000;

export const getOverlappingReservations = (
    targetTableId: string,
    assignedReservations: FloorplanReservationItemDTO[],
    startDate: Date | undefined,
    endDate: Date | undefined,
    excludeReservationId?: string,
): FloorplanReservationItemDTO[] => {
    if (!startDate || !endDate) return [];

    const newStartMs = new Date(startDate).getTime();
    const newEndMs = Math.max(newStartMs, floorToPrevMinute(new Date(endDate).getTime()));

    return assignedReservations.filter((reservationItem) => {
        if (!reservationItem.tableResources?.some((tableResource) => tableResource.id === targetTableId)) return false;
        if (excludeReservationId && reservationItem.reservationId === excludeReservationId) return false;

        const reservationStart = reservationItem.restaurantReservation?.expectedStartDate;
        const reservationEnd = reservationItem.restaurantReservation?.expectedEndDate;
        if (!reservationStart || !reservationEnd) return false;

        const reservationStartMs = new Date(reservationStart).getTime();
        const reservationEndMs = Math.max(
            reservationStartMs,
            floorToPrevMinute(new Date(reservationEnd).getTime()),
        );

        return reservationStartMs < newEndMs && newStartMs < reservationEndMs;
    });
};

const getTableCapacity = (
    targetTableId: string,
    capacity: number,
    overlappingReservations: FloorplanReservationItemDTO[],
): TableCapacityCheck => {
    const currentGuestCount = overlappingReservations.reduce(
        (sum, reservationItem) => {
            const total = getFloorplanGuestCount(reservationItem);
            const tables = reservationItem.tableResources ?? [];
            if (tables.length <= 1) return sum + total;
            // Multi-table: only count this table's share
            let remaining = total;
            const idx = tables.findIndex(tr => tr.id === targetTableId);
            for (let i = 0; i < idx && i < tables.length; i++) {
                const cap = tables[i].capacity ?? 4;
                remaining -= Math.min(remaining, cap);
            }
            return sum + Math.min(remaining, capacity);
        },
        0,
    );

    return {
        capacity,
        currentGuestCount,
        freeSeats: capacity - currentGuestCount,
    };
};

const checkDropConflicts = (
    targetTableId: string,
    neededSeats: number,
    floorplanTables: FloorplanTable[],
    assignedReservations: FloorplanReservationItemDTO[],
    startDate: Date | undefined,
    endDate: Date | undefined,
    excludeReservationId?: string,
): DropConflictResult | null => {
    const targetTable = floorplanTables.find((table) => table.id === targetTableId);
    if (!targetTable) return null;

    const conflicts = getOverlappingReservations(
        targetTableId,
        assignedReservations,
        startDate,
        endDate,
        excludeReservationId,
    );

    const capacityDetails = getTableCapacity(
        targetTableId,
        targetTable.resource.capacity ?? 4,
        conflicts,
    );

    return {
        ...capacityDetails,
        targetTable,
        conflicts,
        hasProblems: conflicts.length > 0 || neededSeats > capacityDetails.freeSeats,
    };
};

export const buildTableReservationsMap = (
    assignedReservations: FloorplanReservationItemDTO[],
): Map<string, FloorplanReservationItemDTO[]> => {
    const tableReservationsMap = new Map<string, FloorplanReservationItemDTO[]>();

    for (const reservationItem of assignedReservations) {
        for (const tableResource of reservationItem.tableResources ?? []) {
            if (!tableResource.id) continue;
            const currentList = tableReservationsMap.get(tableResource.id) ?? [];
            currentList.push(reservationItem);
            tableReservationsMap.set(tableResource.id, currentList);
        }
    }

    tableReservationsMap.forEach((reservations) => {
        reservations.sort((a: FloorplanReservationItemDTO, b: FloorplanReservationItemDTO) => {
            const aStart = a.restaurantReservation?.expectedStartDate
                ? new Date(a.restaurantReservation.expectedStartDate).getTime() : 0;
            const bStart = b.restaurantReservation?.expectedStartDate
                ? new Date(b.restaurantReservation.expectedStartDate).getTime() : 0;
            return aStart - bStart;
        });
    });

    return tableReservationsMap;
};

export const resolveDropDecision = (
    data: DragData,
    targetTableId: string,
    floorplanTables: FloorplanTable[],
    assignedReservations: FloorplanReservationItemDTO[],
): DropDecision => {
    if (data.type === "unassigned" || data.type === "assigned") {
        if (data.type === "assigned" && data.sourceTableId === targetTableId) {
            return {action: "noop"};
        }

        const reservation = data.reservation.restaurantReservation;
        const neededSeats = getFloorplanGuestCount(data.reservation);
        const result = checkDropConflicts(
            targetTableId,
            neededSeats,
            floorplanTables,
            assignedReservations,
            reservation.expectedStartDate,
            reservation.expectedEndDate,
            data.reservation.reservationId,
        );
        if (!result) return {action: "noop"};

        if (neededSeats <= result.freeSeats && result.conflicts.length === 0) {
            return {
                action: "execute_reservation_drop",
                data,
                targetTableId,
                result,
                neededSeats,
            };
        }

        if (neededSeats > result.freeSeats) {
            return {
                action: "open_conflict_dialog",
                data,
                targetTableId,
                result,
                neededSeats,
            };
        }

        return {
            action: "open_shared_table_dialog",
            data,
            targetTableId,
            result,
        };
    }

    const neededSeats = getGuestCount(data.halfboard.accommodationReservation);
    const result = checkDropConflicts(
        targetTableId,
        neededSeats,
        floorplanTables,
        assignedReservations,
        undefined,
        undefined,
    );
    if (!result) return {action: "noop"};

    if (result.hasProblems) {
        return {
            action: "open_conflict_dialog",
            data,
            targetTableId,
            result,
            neededSeats,
        };
    }

    return {
        action: "open_halfboard_period",
        halfboard: data.halfboard,
        targetTable: result.targetTable,
    };
};

export const buildPreviewCardProps = (
    dragData: DragData | undefined,
    targetTableName: string,
    floorplanTables: FloorplanTable[],
): ReservationPreviewCardContentProps => {
    if (!dragData) {
        return {
            reservation: undefined,
            profiles: [],
            targetTableName,
        };
    }

    if (dragData.type === "halfboard") {
        return {
            reservation: dragData.halfboard.accommodationReservation,
            profiles: dragData.halfboard.profiles,
            accommodationResources: dragData.halfboard.accommodationResources,
            ageCategories: dragData.halfboard.ageCategories,
            accommodationServiceName: getHalfboardRateLabel(dragData.halfboard),
            targetTableName,
        };
    }

    return {
        reservation: dragData.reservation.restaurantReservation,
        profiles: dragData.reservation.profiles ?? [],
        accommodationResources: dragData.reservation.accommodationResources,
        ageCategories: dragData.reservation.ageCategories,
        guestCounts: getFloorplanGuestCounts(dragData.reservation),
        aliasAgeCategories: dragData.reservation.aliasAgeCategories,
        sourceTableName: dragData.type === "assigned"
            ? floorplanTables.find((table) => table.id === dragData.sourceTableId)?.resource.name
            : undefined,
        targetTableName,
    };
};

/**
 * True when confirming this drop is the last step before the reservation is persisted, i.e. the
 * dialog must offer the "Save & send email" choice itself.
 *
 * Halfboard drags still pass through the reservation dialog and grouped reservations through the
 * linked-reservations dialog — both ask for the email choice at their own confirm step, so the
 * earlier dialogs must not ask twice.
 */
export const dropDecidesGuestEmail = (dragData: DragData | undefined): boolean => {
    if (!dragData || dragData.type === "halfboard") return false;
    return !dragData.reservation.restaurantReservation?.reservationGroupId;
};
